/**
 * The browser tab and home-screen icon follow the branding logo (issue #20),
 * same as the nav mark: an organization that uploads its logo gets it in the
 * tab too, and clearing the logo puts the app's diamond back.
 *
 * This is mounted on `App` rather than `Layout` because `/login` and the rest
 * of the auth pages are a sibling shell — an icon set inside `Layout` would
 * quietly stay the app's own on four routes.
 */
import { useEffect } from "react";
import { useBranding } from "./siteSettings";

/**
 * The app's own mark. Deliberately duplicated in `index.html`, which is the
 * icon a visitor sees while the bundle loads — change both or neither. A test
 * in `appIcon.test.ts` fails if they drift apart.
 */
export const DEFAULT_ICON_HREF =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='8' fill='%230b6fc4'/%3E%3Cpath d='M16 7 25 16 16 25 7 16Z' fill='none' stroke='white' stroke-width='2' stroke-linejoin='round'/%3E%3Ccircle cx='16' cy='16' r='2.2' fill='white'/%3E%3C/svg%3E";

/** The tab icon for these settings: the uploaded logo, or the app's diamond. */
export function iconHref(logoUrl?: string | null): string {
  return logoUrl?.trim() || DEFAULT_ICON_HREF;
}

/**
 * The iOS home-screen icon, or null to leave the platform to its own devices.
 * Only an upload qualifies: uploads are raster (see `IMAGE_EXTENSIONS`), which
 * iOS renders, and the fallback diamond is an SVG, which it does not.
 */
export function homeScreenIconHref(logoUrl?: string | null): string | null {
  return logoUrl?.trim() || null;
}

/** Point `<link rel={rel}>` at `href`, creating it — or drop it for a null. */
function setIconLink(rel: string, href: string | null): void {
  const existing = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!href) {
    existing?.remove();
    return;
  }
  const link = existing ?? document.head.appendChild(document.createElement("link"));
  link.rel = rel;
  link.href = href;
}

/** Keep the tab and home-screen icons on the current branding logo. */
export function useAppIcon(): void {
  const { logoUrl } = useBranding();
  useEffect(() => {
    setIconLink("icon", iconHref(logoUrl));
    setIconLink("apple-touch-icon", homeScreenIconHref(logoUrl));
  }, [logoUrl]);
}
